/**
 * structuredData — JSON-LD builders (schema.org). Pure functions returning
 * plain objects; serialized by <Seo jsonLd={...} />. High GEO value: AI engines
 * and Google rich results read these.
 */
import { siteConfig } from "./siteConfig";

const abs = (path = "") => (path.startsWith("http") ? path : `${siteConfig.baseUrl}${path}`);

export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteConfig.organization.name,
    url: siteConfig.organization.url,
    logo: siteConfig.organization.logo,
    sameAs: siteConfig.organization.sameAs,
  };
}

export function webSiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteConfig.siteName,
    url: siteConfig.baseUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: `${siteConfig.baseUrl}/blog?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

/** Article / BlogPosting for blog, guides, use-cases. */
export function articleLd({
  type = "Article",
  title,
  description,
  path,
  image,
  datePublished,
  dateModified,
}) {
  return {
    "@context": "https://schema.org",
    "@type": type,
    headline: title,
    description,
    image: abs(image || siteConfig.defaultOgImage),
    datePublished,
    dateModified: dateModified || datePublished,
    mainEntityOfPage: { "@type": "WebPage", "@id": abs(path) },
    author: { "@type": "Organization", name: siteConfig.siteName },
    publisher: organizationLd(),
  };
}

/** FAQPage from [{q, a}] — the format AI engines lift most readily. */
export function faqPageLd(faq = []) {
  if (!faq.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/** BreadcrumbList from [{name, path}]. */
export function breadcrumbLd(items = []) {
  if (!items.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: abs(it.path),
    })),
  };
}

/** SoftwareApplication with the real plan offers (pricing / feature pages). */
export function softwareApplicationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: siteConfig.siteName,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: siteConfig.baseUrl,
    description: siteConfig.defaultDescription,
    featureList: [
      "Branded short links",
      "Branded QR codes",
      "Link-in-bio pages",
      "Smart routing (geo, device, A/B, time, rotator, returning-visitor)",
      "Real-time click analytics with heatmaps",
      "Campaigns and UTM tracking",
      "Retargeting pixels (Meta, Google, Pinterest, TikTok)",
      "CTA overlay banners",
      "Custom domains",
      "REST API and webhooks",
    ],
    offers: [
      { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD" },
      { "@type": "Offer", name: "Pro", price: "19", priceCurrency: "USD" },
      { "@type": "Offer", name: "Enterprise", price: "49", priceCurrency: "USD" },
    ],
  };
}

/** ItemList for comparison / alternative listicles. */
export function itemListLd({ name, path, items = [] }) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url: abs(path),
    numberOfItems: items.length,
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      url: it.url,
    })),
  };
}
